import React, { useRef, useEffect, useState } from 'react'

import clsx from 'clsx'

import { useEventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import useCarouselScroll from './useCarouselScroll'
import CarouselButton from '../CarouselButton'

import classes from './Carousel.module.scss'

const DIRECTIONS = {
  LEFT: 'left',
  RIGHT: 'right',
}

const SCROLL_OFFSET = 30

/** @type {import("./types").default} */
const Carousel = ({ children, orientation = 'horizontal', forceArrows = false }) => {
  const { viewport, up } = useViewport()
  const [scrollDistance, setScrollDistance] = useState(0)
  const ref = useRef(null)
  const { scroll } = useCarouselScroll(ref, orientation)
  const { addEvent } = useEventContext()

  const showArrows = up('md') || forceArrows
  const isHorizontal = orientation === 'horizontal'

  useEffect(() => {
    const distance = isHorizontal
      ? ref.current?.scrollWidth - ref.current?.clientWidth
      : ref.current?.scrollHeight - ref.current?.clientHeight
    setScrollDistance(distance)
  }, [viewport, children, isHorizontal])

  const scrollDirection = dir => () => {
    const firstChildrenWidth = ref.current?.firstElementChild.offsetWidth
    const to =
      dir === DIRECTIONS.RIGHT
        ? scroll + firstChildrenWidth + SCROLL_OFFSET
        : scroll - firstChildrenWidth - SCROLL_OFFSET
    const scrollPosition = {
      [isHorizontal ? 'left' : 'top']: to,
      behavior: 'smooth',
    }
    ref.current?.scrollTo(scrollPosition)
  }

  return (
    <div className={clsx('Carousel', classes.root)}>
      <div
        data-testid="carouselContainer"
        ref={ref}
        role="region"
        tabIndex={0}
        className={clsx(classes.imageContainer, { [classes.verticalContainer]: !isHorizontal })}
      >
        {children}
      </div>
      {!!scrollDistance && (
        <>
          <button
            onClick={showArrows ? scrollDirection(DIRECTIONS.LEFT) : null}
            className={clsx(classes.control, { [classes.visible]: scroll !== 0, [classes.top]: !isHorizontal })}
            {...addEvent({
              engagementComponent: 'product_media',
              engagementDestination: isHorizontal ? 'slide-to_left' : 'slide-to_top',
              engagementDetails: 'slider-arrow',
              name: 'engagement_product_click',
            })}
            data-testid="carousel-slide-left"
          >
            {showArrows && <CarouselButton />}
          </button>

          <button
            onClick={showArrows ? scrollDirection(DIRECTIONS.RIGHT) : null}
            className={clsx(classes.control, classes.right, {
              [classes.visible]: Math.ceil(scroll) !== Math.ceil(scrollDistance),
              [classes.bottom]: !isHorizontal,
            })}
            {...addEvent({
              engagementComponent: 'product_media',
              engagementDestination: isHorizontal ? 'slide-to_right' : 'slide-to_bottom',
              engagementDetails: 'slider-arrow',
              name: 'engagement_product_click',
            })}
            data-testid="carousel-slide-right"
          >
            {showArrows && <CarouselButton isRight />}
          </button>
        </>
      )}
    </div>
  )
}

export default Carousel
